import { useState, useEffect, useMemo } from 'react';
import {
  Layers, Sparkles, AlertTriangle, TrendingUp, RefreshCw, Loader2,
  Droplets, Calculator, Target, Zap, Info, ChevronDown, ChevronUp,
  Rocket, Coins, Building2, Diamond, CheckCircle2, BookOpen,
  Cpu,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { computeDexLiquidity, getDexSpotPrice } from '../services/api';
import { InfoTip } from '../components/ui';

const COMMON_SYMBOLS = ['ETH', 'BTC', 'SOL', 'MATIC', 'ARB', 'OP', 'AVAX', 'BNB', 'LINK', 'UNI'];

const FEE_TIERS = [
  { value: 0.0001, label: '0.01% — stablecoins' },
  { value: 0.0005, label: '0.05% — correlated' },
  { value: 0.003,  label: '0.30% — standard' },
  { value: 0.01,   label: '1.00% — exotic' },
];

// Preset scenarios tuned to common issuer launch patterns. Picking one
// pre-fills the inputs so a user doesn't have to guess sensible numbers
// from scratch — they can adjust from a real-world baseline.
const PRESETS = [
  {
    id: 'utility',
    label: 'Utility / platform token',
    icon: Cpu,
    accent: 'cyan',
    blurb: 'Powers a product or platform. Retail buyers paying for fees, governance, or access.',
    inputs: { targetDailyVolumeUsd: 100000, avgTradeSizeUsd: 300, maxSlippagePct: 2, feeTier: 0.003 },
  },
  {
    id: 'memecoin',
    label: 'Memecoin / new launch',
    icon: Rocket,
    accent: 'amber',
    blurb: 'Brand-new token, retail buyers, high volatility',
    inputs: { targetDailyVolumeUsd: 50000, avgTradeSizeUsd: 200, maxSlippagePct: 3, feeTier: 0.01 },
  },
  {
    id: 'midcap',
    label: 'Mid-cap project token',
    icon: Coins,
    accent: 'cyan',
    blurb: 'Established project, mix of retail + institutional, moderate volatility',
    inputs: { targetDailyVolumeUsd: 500000, avgTradeSizeUsd: 1000, maxSlippagePct: 1.5, feeTier: 0.003 },
  },
  {
    id: 'stable',
    label: 'Stablecoin pair',
    icon: Diamond,
    accent: 'emerald',
    blurb: 'USDC/USDT or similar — large trades, near-zero slippage',
    inputs: { targetDailyVolumeUsd: 1000000, avgTradeSizeUsd: 5000, maxSlippagePct: 0.05, feeTier: 0.0005 },
  },
  {
    id: 'institutional',
    label: 'Institutional / large-cap',
    icon: Building2,
    accent: 'purple',
    blurb: 'Heavy daily volume, large average trade, professional traders',
    inputs: { targetDailyVolumeUsd: 5000000, avgTradeSizeUsd: 10000, maxSlippagePct: 1, feeTier: 0.003 },
  },
];

function fmtUsd(v, dp = 0) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
}
function fmtPct(v, dp = 2) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${Number(v).toFixed(dp)}%`;
}

// Recommends the V3 range option that best fits the user's risk profile.
// Heuristic: tightest range whose required TVL is at least 1% of daily volume
// (so the pool has SOME depth) and whose width is no narrower than ±5%
// (anything narrower exits range too easily for a normal token).
function recommendV3Option(v3Options, targetDailyVolumeUsd) {
  if (!v3Options?.length || !targetDailyVolumeUsd) return null;
  const usable = v3Options.filter(o => o.widthPct != null && o.widthPct >= 5);
  const sorted = [...usable].sort((a, b) => a.requiredTvlUsd - b.requiredTvlUsd);
  const minTvl = targetDailyVolumeUsd * 0.01;
  const pick = sorted.find(o => o.requiredTvlUsd >= minTvl) || sorted[sorted.length - 1] || null;
  return pick;
}

export default function DexLiquidityOptimizerPage() {
  const [tokenSymbol, setTokenSymbol] = useState('ETH');
  const [tokenPriceUsd, setTokenPriceUsd] = useState('');
  const [priceFetching, setPriceFetching] = useState(false);
  const [priceSource, setPriceSource] = useState(null);

  const [targetDailyVolumeUsd, setTargetDailyVolumeUsd] = useState(250000);
  const [avgTradeSizeUsd, setAvgTradeSizeUsd] = useState(500);
  const [maxSlippagePct, setMaxSlippagePct] = useState(2);
  const [feeTier, setFeeTier] = useState(0.003);

  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState(null);
  const [appliedPreset, setAppliedPreset] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(true);

  async function fetchPrice(symbol = tokenSymbol) {
    if (!symbol) return;
    setPriceFetching(true);
    setError(null);
    try {
      const res = await getDexSpotPrice(symbol);
      const q = res?.data;
      if (q?.price && Number.isFinite(q.price) && q.price > 0) {
        setTokenPriceUsd(Number(q.price.toFixed(6)));
        setPriceSource(q.source || 'live');
      } else {
        setTokenPriceUsd('');
        setPriceSource('unavailable');
      }
    } catch (e) {
      setError(e.message || 'Failed to fetch spot price');
      setPriceSource('unavailable');
    } finally {
      setPriceFetching(false);
    }
  }
  useEffect(() => { fetchPrice('ETH'); /* initial */ }, []);

  function applyPreset(preset) {
    setAppliedPreset(preset.id);
    setTargetDailyVolumeUsd(preset.inputs.targetDailyVolumeUsd);
    setAvgTradeSizeUsd(preset.inputs.avgTradeSizeUsd);
    setMaxSlippagePct(preset.inputs.maxSlippagePct);
    setFeeTier(preset.inputs.feeTier);
  }

  async function handleCompute() {
    setError(null);
    setComputing(true);
    try {
      const res = await computeDexLiquidity({
        tokenSymbol,
        tokenPriceUsd: Number(tokenPriceUsd),
        targetDailyVolumeUsd: Number(targetDailyVolumeUsd),
        avgTradeSizeUsd: Number(avgTradeSizeUsd),
        maxSlippagePct: Number(maxSlippagePct),
        feeTier: Number(feeTier),
      });
      setResult(res?.data || res);
    } catch (e) {
      setError(e?.message || 'Computation failed');
    } finally {
      setComputing(false);
    }
  }

  const inputsValid = useMemo(() =>
    Number(tokenPriceUsd) > 0 &&
    Number(targetDailyVolumeUsd) > 0 &&
    Number(avgTradeSizeUsd) > 0 &&
    Number(maxSlippagePct) > 0 &&
    Number(maxSlippagePct) < 50,
  [tokenPriceUsd, targetDailyVolumeUsd, avgTradeSizeUsd, maxSlippagePct]);

  const recommendation = useMemo(
    () => result ? recommendV3Option(result.v3Options, result.inputs.targetDailyVolumeUsd) : null,
    [result]
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-cyan-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-cyan-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/20 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
              <Droplets className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-white">DEX Liquidity Depth Optimizer</h1>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-mono">Real math · No estimates</span>
              </div>
              <p className="text-xs text-cyan-200/70 mt-1 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Elite · Issuer Tools · Tell us your launch plan; we tell you exactly how much liquidity to deploy
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* What is this? */}
      <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 overflow-hidden">
        <button
          onClick={() => setAboutOpen(o => !o)}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-dark-700/30 transition"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-cyan-400" />
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-white">What is this tool — and how do I use it?</div>
              <div className="text-[11px] text-gray-500">Plain-English walkthrough · 30 seconds</div>
            </div>
          </div>
          {aboutOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {aboutOpen && (
          <div className="px-5 pb-5 border-t border-dark-700/40 pt-4 space-y-4">
            <div>
              <div className="text-xs font-semibold text-cyan-300 uppercase tracking-wider mb-1.5">The problem</div>
              <p className="text-sm text-gray-300 leading-relaxed">
                You're launching a token (or already have one) and need to deploy liquidity on a DEX like Uniswap. The hardest question is: <span className="text-white font-semibold">how much liquidity is enough?</span> Too little and buyers face brutal slippage; too much and your capital sits idle earning nothing.
              </p>
            </div>
            <div>
              <div className="text-xs font-semibold text-cyan-300 uppercase tracking-wider mb-1.5">What this tool does</div>
              <p className="text-sm text-gray-300 leading-relaxed">
                Given the volume you expect, the typical trade size, and the slippage you want buyers to feel, it computes the <span className="text-white">exact pool size</span> needed — using the real Uniswap V2/V3 formulas, not estimates. It also shows how much LESS capital you need if you use V3 concentrated ranges, and forecasts your fee revenue.
              </p>
            </div>
            <div>
              <div className="text-xs font-semibold text-cyan-300 uppercase tracking-wider mb-1.5">How to use it in 4 steps</div>
              <ol className="text-sm text-gray-300 leading-relaxed space-y-1.5 list-decimal pl-5">
                <li>Pick a <span className="text-white">scenario preset</span> below that matches your launch (or skip and enter custom numbers).</li>
                <li>Set the <span className="text-white">spot price</span> of your token (auto-fetched for major coins).</li>
                <li>Adjust the <span className="text-white">volume target, avg trade size, and slippage</span> to match your expectations.</li>
                <li>Click <span className="text-white">Compute</span>. You'll get the V2 pool size, six V3 range options, the slippage curve, and a fee-revenue forecast.</li>
              </ol>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-200/90 leading-relaxed">
                <span className="font-semibold">How to read this:</span> Fee forecasts assume the volume target is actually achieved. Capital efficiency in V3 assumes price stays inside the chosen range — if it exits, the position stops earning fees until you rebalance.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Quick-start presets */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-bold text-white">Start with a scenario</h2>
            <p className="text-[11px] text-gray-500 mt-0.5">Pick the one closest to your launch — we'll fill in sensible numbers you can tweak.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {PRESETS.map(p => {
            const Icon = p.icon;
            const isActive = appliedPreset === p.id;
            const accents = {
              amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-300', icon: 'text-amber-400', activeBorder: 'border-amber-500/60' },
              cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-300', icon: 'text-cyan-400', activeBorder: 'border-cyan-500/60' },
              emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-300', icon: 'text-emerald-400', activeBorder: 'border-emerald-500/60' },
              purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-300', icon: 'text-purple-400', activeBorder: 'border-purple-500/60' },
            }[p.accent];
            return (
              <button
                key={p.id}
                onClick={() => applyPreset(p)}
                className={`text-left p-4 rounded-xl border bg-dark-800/60 hover:bg-dark-700/40 transition ${isActive ? accents.activeBorder : 'border-dark-700/50'}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-7 h-7 rounded-lg ${accents.bg} ${accents.border} border flex items-center justify-center`}>
                    <Icon className={`w-3.5 h-3.5 ${accents.icon}`} />
                  </div>
                  <div className={`text-xs font-semibold ${accents.text}`}>{p.label}</div>
                  {isActive && <CheckCircle2 className={`w-3.5 h-3.5 ${accents.icon} ml-auto`} />}
                </div>
                <p className="text-[10px] text-gray-500 leading-relaxed mb-2">{p.blurb}</p>
                <div className="text-[10px] text-gray-400 font-mono space-y-0.5">
                  <div>Vol: ${p.inputs.targetDailyVolumeUsd.toLocaleString()}/day</div>
                  <div>Slippage: {p.inputs.maxSlippagePct}% · Fee: {(p.inputs.feeTier * 100).toFixed(2)}%</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Inputs */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
            <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
              <Calculator className="w-4 h-4 text-cyan-400" /> Your launch plan
            </h2>

            <div className="space-y-4">
              {/* Symbol + price */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Token symbol</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {COMMON_SYMBOLS.map(s => (
                    <button
                      key={s}
                      onClick={() => { setTokenSymbol(s); fetchPrice(s); }}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-mono transition ${
                        tokenSymbol === s
                          ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40'
                          : 'bg-dark-700/60 text-gray-400 hover:text-white border border-dark-600'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={tokenSymbol}
                  onChange={e => setTokenSymbol(e.target.value.toUpperCase())}
                  onBlur={() => fetchPrice()}
                  placeholder="Type any symbol (e.g. MYTOKEN)"
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-cyan-400/40 focus:outline-none"
                />
                <p className="text-[10px] text-gray-500 mt-1">Used to label results. For unlisted tokens, just type the ticker and enter the price below manually.</p>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 flex items-center justify-between">
                  <span>Token spot price (USD)</span>
                  <button
                    onClick={() => fetchPrice()}
                    disabled={priceFetching}
                    className="text-[10px] text-cyan-400 hover:text-cyan-300 disabled:opacity-40 flex items-center gap-1"
                  >
                    {priceFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Refresh live price
                  </button>
                </label>
                <input
                  type="number"
                  step="0.000001"
                  value={tokenPriceUsd}
                  onChange={e => setTokenPriceUsd(e.target.value)}
                  placeholder="0.00"
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-cyan-400/40 focus:outline-none"
                />
                {priceSource && priceSource !== 'unavailable' && (
                  <p className="text-[10px] text-emerald-400/70 mt-1">✓ Live market price</p>
                )}
                {priceSource === 'unavailable' && (
                  <p className="text-[10px] text-amber-400/70 mt-1">⚠ No live quote available for "{tokenSymbol}". Enter price manually.</p>
                )}
              </div>

              <div className="pt-2 border-t border-dark-700/40">
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                  Target daily volume (USD)
                  <InfoTip side="right">HONEST week-2 expectation, not launch-day spike. Optimistic numbers here lead to over-deploying 5-10× more capital than needed.</InfoTip>
                </label>
                <input
                  type="number"
                  value={targetDailyVolumeUsd}
                  onChange={e => setTargetDailyVolumeUsd(e.target.value)}
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-cyan-400/40 focus:outline-none"
                />
                <p className="text-[10px] text-gray-500 mt-1">Realistic volume after launch settles. New tokens often hit ${(50_000).toLocaleString()}–${(500_000).toLocaleString()} on day one if marketed well.</p>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                  Average trade size (USD)
                  <InfoTip side="right">Pool size scales LINEARLY with this. Doubling it doubles required TVL. Most retail is $100-500 — overstating leads to wasted capital.</InfoTip>
                </label>
                <input
                  type="number"
                  value={avgTradeSizeUsd}
                  onChange={e => setAvgTradeSizeUsd(e.target.value)}
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-cyan-400/40 focus:outline-none"
                />
                <p className="text-[10px] text-gray-500 mt-1">Retail = $100–500 · Active traders = $1k–5k · Whales = $10k+. Pool size scales linearly with this.</p>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                  Max slippage on average trade (%)
                  <InfoTip side="right">% price-impact the average buyer feels. 1-3% = normal for new tokens. Under 0.5% = whale-level capital required. Tighter slippage = bigger pool.</InfoTip>
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={maxSlippagePct}
                  onChange={e => setMaxSlippagePct(e.target.value)}
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-cyan-400/40 focus:outline-none"
                />
                <p className="text-[10px] text-gray-500 mt-1">What % price-impact the average buyer should feel. Tighter = bigger pool needed. 1–3% is normal for new tokens; &lt;0.5% requires whale-level capital.</p>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                  Uniswap fee tier
                  <InfoTip side="right">Pick the tier your competitors use. Aggregators route to the cheapest tier — if a 0.30% pool exists, your 1.00% pool sees almost no volume.</InfoTip>
                </label>
                <select
                  value={feeTier}
                  onChange={e => setFeeTier(Number(e.target.value))}
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:border-cyan-400/40 focus:outline-none"
                >
                  {FEE_TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <p className="text-[10px] text-gray-500 mt-1">Higher fees = more revenue per trade, but trades route to lower-fee tiers if available. 0.30% is the default for non-stable pairs.</p>
              </div>

              <button
                onClick={handleCompute}
                disabled={!inputsValid || computing}
                className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-cyan-500 text-dark-900 hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed font-bold text-sm transition"
              >
                {computing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {computing ? 'Computing…' : 'Compute optimal liquidity'}
              </button>

              {error && (
                <div className="mt-2 bg-red-500/5 border border-red-500/20 rounded-lg p-2 text-xs text-red-300">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Outputs */}
        <div className="lg:col-span-2 space-y-4">
          {!result && (
            <div className="bg-dark-800/40 rounded-2xl border border-dark-700/40 border-dashed p-10 text-center">
              <Target className="w-10 h-10 text-cyan-400/40 mx-auto mb-3" />
              <p className="text-sm text-gray-300 font-medium">Ready when you are.</p>
              <p className="text-[12px] text-gray-500 mt-2 max-w-md mx-auto leading-relaxed">
                Pick a scenario above or enter custom numbers, then click <span className="text-white font-semibold">Compute optimal liquidity</span>. You'll see the V2 pool size, six V3 concentrated-range options, the slippage curve across trade sizes, and a fee-revenue forecast.
              </p>
            </div>
          )}

          {result && (
            <>
              {/* Headline */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatCard
                  label="V2 full-range TVL needed"
                  value={fmtUsd(result.v2RequiredTvlUsd)}
                  subtext={`for ${result.inputs.maxSlippagePct}% slippage on $${result.inputs.avgTradeSizeUsd.toLocaleString()} trades`}
                  accent="cyan"
                />
                <StatCard
                  label="Daily fee revenue (forecast)"
                  value={fmtUsd(result.feeForecast.dailyUsd, 2)}
                  subtext={`${(result.inputs.feeTier * 100).toFixed(2)}% × $${result.inputs.targetDailyVolumeUsd.toLocaleString()} volume`}
                  accent="emerald"
                />
                <StatCard
                  label="APY on V2 TVL"
                  value={fmtPct(result.feeForecast.apyOnV2TvlPct, 1)}
                  subtext={`assumes volume target hit`}
                  accent={result.feeForecast.apyOnV2TvlPct > 100 ? 'emerald' : 'amber'}
                />
              </div>

              {/* Plain-English summary */}
              <div className="bg-gradient-to-br from-cyan-500/10 to-emerald-500/5 border border-cyan-500/30 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="w-4 h-4 text-cyan-300" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-sm font-bold text-white">What this means for your launch</h3>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      To support <span className="text-white font-semibold">${result.inputs.targetDailyVolumeUsd.toLocaleString()}/day</span> in volume with average buyers experiencing at most <span className="text-white font-semibold">{result.inputs.maxSlippagePct}% slippage</span> on a <span className="text-white font-semibold">${result.inputs.avgTradeSizeUsd.toLocaleString()}</span> trade, you need a Uniswap V2 pool of <span className="text-cyan-300 font-mono font-semibold">{fmtUsd(result.v2RequiredTvlUsd)}</span> — split 50/50 between {result.inputs.tokenSymbol || 'your token'} and the stable side.
                    </p>
                    {recommendation && (
                      <p className="text-xs text-gray-300 leading-relaxed">
                        Using Uniswap V3 with a <span className="text-white font-semibold">{recommendation.rangeLabel.toLowerCase()}</span> concentrated range, you only need <span className="text-emerald-300 font-mono font-semibold">{fmtUsd(recommendation.requiredTvlUsd)}</span> — a <span className="text-emerald-300 font-semibold">{recommendation.capitalEfficiency}× capital saving</span>. The tradeoff: if price exits the range, the position stops earning fees until you rebalance.
                      </p>
                    )}
                    <p className="text-xs text-gray-300 leading-relaxed">
                      At your volume target, the pool earns <span className="text-emerald-300 font-mono font-semibold">{fmtUsd(result.feeForecast.dailyUsd, 0)}/day</span> in fees — an APY of <span className="text-white font-semibold">{fmtPct(result.feeForecast.apyOnV2TvlPct, 1)}</span> on the V2 capital.
                    </p>
                  </div>
                </div>
              </div>

              {/* Slippage curve */}
              <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-cyan-400" /> Slippage curve at recommended TVL
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Your average trade ($&nbsp;{result.inputs.avgTradeSizeUsd.toLocaleString()}) sits at <strong className="text-cyan-300">{result.inputs.maxSlippagePct}%</strong>. Larger trades slip more — see what a whale-sized trade does.
                </p>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={result.slippageCurve}>
                      <defs>
                        <linearGradient id="slipGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="tradeSizeUsd"
                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                        tickFormatter={v => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                        tickFormatter={v => `${v}%`}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' }}
                        labelFormatter={v => `Trade: $${Number(v).toLocaleString()}`}
                        formatter={(v) => [`${v}% slippage`, '']}
                      />
                      <ReferenceLine y={result.inputs.maxSlippagePct} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: `${result.inputs.maxSlippagePct}% target`, fill: '#f59e0b', fontSize: 10, position: 'insideTopRight' }} />
                      <Area type="monotone" dataKey="slippagePct" stroke="#06b6d4" strokeWidth={2} fill="url(#slipGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* V3 options */}
              <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
                <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-cyan-400" /> Uniswap V3 concentrated-liquidity options
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Same effective depth as the V2 pool above, but capital-efficient via concentrated ranges. Tighter ranges = much less capital, but the position becomes inactive (and stops earning fees) if price exits the range. The <span className="text-emerald-300 font-semibold">★ recommended</span> row is our heuristic best fit for your inputs.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-dark-700/40">
                        <th className="text-left py-2 px-2">Range</th>
                        <th className="text-right py-2 px-2">Efficiency</th>
                        <th className="text-right py-2 px-2">Capital needed</th>
                        <th className="text-right py-2 px-2">Stable side</th>
                        <th className="text-right py-2 px-2">Token side</th>
                        <th className="text-right py-2 px-2">{result.inputs.tokenSymbol || 'Token'} qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.v3Options.map((o, i) => {
                        const isV2 = o.widthPct == null;
                        const isRecommended = recommendation && o.rangeLabel === recommendation.rangeLabel;
                        return (
                          <tr
                            key={i}
                            className={`border-b border-dark-800/50 ${
                              isRecommended ? 'bg-emerald-500/5' :
                              isV2 ? 'bg-dark-700/20' : ''
                            }`}
                          >
                            <td className="py-2 px-2 text-white font-medium">
                              {isRecommended && <span className="text-emerald-400 mr-1">★</span>}
                              {o.rangeLabel}
                            </td>
                            <td className="py-2 px-2 text-right font-mono text-cyan-300">{o.capitalEfficiency}x</td>
                            <td className={`py-2 px-2 text-right font-mono font-bold ${isRecommended ? 'text-emerald-300' : 'text-white'}`}>{fmtUsd(o.requiredTvlUsd, 0)}</td>
                            <td className="py-2 px-2 text-right font-mono text-gray-400">{fmtUsd(o.stableSideUsd, 0)}</td>
                            <td className="py-2 px-2 text-right font-mono text-gray-400">{fmtUsd(o.tokenSideUsd, 0)}</td>
                            <td className="py-2 px-2 text-right font-mono text-gray-400">{Number(o.tokenSideQty).toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Fee forecast detail */}
              <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-400" /> Fee revenue forecast
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <SmallStat label="Daily" value={fmtUsd(result.feeForecast.dailyUsd, 2)} />
                  <SmallStat label="Monthly" value={fmtUsd(result.feeForecast.monthlyUsd, 0)} />
                  <SmallStat label="Annual" value={fmtUsd(result.feeForecast.annualUsd, 0)} />
                </div>
                <p className="text-[11px] text-gray-500 mt-3">
                  Math: <span className="font-mono">${result.inputs.targetDailyVolumeUsd.toLocaleString()} daily × {(result.inputs.feeTier * 100).toFixed(2)}% fee tier</span>. Real fees scale with real volume — this is the forecast assuming the target is achieved.
                </p>
              </div>

              {/* Warnings + assumptions */}
              {result.warnings?.length > 0 && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 space-y-2">
                  {result.warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-200/90">{w}</p>
                    </div>
                  ))}
                </div>
              )}
              {result.assumptions?.length > 0 && (
                <div className="bg-dark-900/40 rounded-xl border border-dark-700/40 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="w-3.5 h-3.5 text-gray-500" />
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">Assumptions</p>
                  </div>
                  <ul className="space-y-1">
                    {result.assumptions.map((a, i) => (
                      <li key={i} className="text-[11px] text-gray-400 pl-1 leading-relaxed">• {a}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, subtext, accent }) {
  const color = accent === 'cyan' ? 'text-cyan-300' : accent === 'emerald' ? 'text-emerald-400' : accent === 'amber' ? 'text-amber-300' : 'text-white';
  return (
    <div className="bg-dark-800/60 rounded-xl border border-dark-700/50 p-4">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-xl font-bold font-mono mt-1 ${color}`}>{value}</p>
      {subtext && <p className="text-[10px] text-gray-500 mt-1">{subtext}</p>}
    </div>
  );
}

function SmallStat({ label, value }) {
  return (
    <div className="bg-dark-700/30 rounded-lg p-3 text-center">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className="text-base font-bold font-mono text-emerald-400 mt-0.5">{value}</p>
    </div>
  );
}
